// ═══════════════════════════════════════════════════════════════
// Exchange Rates — Static Approximations (All relative to USD)
// Used for display-only conversion on the storefront
// ═══════════════════════════════════════════════════════════════

/**
 * Static exchange rates relative to USD
 * These are approximate values for display purposes only.
 * The store always transacts in its base currency.
 * 
 * Last updated: 2026-02-22
 */
export const EXCHANGE_RATES: Record<string, number> = {
    // === Major Currencies ===
    USD: 1,
    EUR: 0.92,
    GBP: 0.79,
    CAD: 1.44,
    CHF: 0.89,
    JPY: 150.2,
    CNY: 7.25,
    AUD: 1.57,

    // === North Africa ===
    DZD: 134.5,   // دينار جزائري
    MAD: 9.95,    // درهم مغربي
    TND: 3.12,    // دينار تونسي
    EGP: 50.5,    // جنيه مصري
    LYD: 4.85,    // دينار ليبي

    // === Gulf Countries ===
    SAR: 3.75,    // ريال سعودي
    AED: 3.67,    // درهم إماراتي
    QAR: 3.64,    // ريال قطري
    KWD: 0.31,    // دينار كويتي
    OMR: 0.38,    // ريال عماني
    BHD: 0.38,    // دينار بحريني
    YER: 250.0,   // ريال يمني

    // === Levant ===
    SYP: 13000,   // ليرة سورية
    LBP: 89500,   // ليرة لبنانية
    JOD: 0.71,    // دينار أردني
    IQD: 1310,    // دينار عراقي

    // === Other ===
    TRY: 36.2,    // ليرة تركية
    PKR: 278.5,   // روبية باكستانية
    INR: 83.4,    // روبية هندية
};

/**
 * Country code → preferred currency mapping
 * Used for auto-detection based on visitor's locale
 */
export const COUNTRY_CURRENCY_MAP: Record<string, string> = {
    DZ: 'DZD', MA: 'MAD', TN: 'TND', EG: 'EGP', LY: 'LYD',
    SA: 'SAR', AE: 'AED', QA: 'QAR', KW: 'KWD', OM: 'OMR',
    BH: 'BHD', YE: 'YER', SY: 'SYP', LB: 'LBP', JO: 'JOD',
    IQ: 'IQD', TR: 'TRY', US: 'USD', GB: 'GBP', FR: 'EUR',
    DE: 'EUR', ES: 'EUR', IT: 'EUR', NL: 'EUR', BE: 'EUR',
    PT: 'EUR', AT: 'EUR', IE: 'EUR', FI: 'EUR', CA: 'CAD',
    AU: 'AUD', JP: 'JPY', CN: 'CNY', CH: 'CHF', PK: 'PKR',
    IN: 'INR',
};

/**
 * Currency flag emojis for display
 */
export const CURRENCY_FLAGS: Record<string, string> = {
    DZD: '🇩🇿', MAD: '🇲🇦', TND: '🇹🇳', EGP: '🇪🇬', LYD: '🇱🇾',
    SAR: '🇸🇦', AED: '🇦🇪', QAR: '🇶🇦', KWD: '🇰🇼', OMR: '🇴🇲',
    BHD: '🇧🇭', YER: '🇾🇪', SYP: '🇸🇾', LBP: '🇱🇧', JOD: '🇯🇴',
    IQD: '🇮🇶', TRY: '🇹🇷', USD: '🇺🇸', EUR: '🇪🇺', GBP: '🇬🇧',
    CAD: '🇨🇦', CHF: '🇨🇭', JPY: '🇯🇵', CNY: '🇨🇳', AUD: '🇦🇺',
    PKR: '🇵🇰', INR: '🇮🇳',
};

/**
 * Currency display names (Arabic)
 */
export const CURRENCY_NAMES_AR: Record<string, string> = {
    DZD: 'دينار جزائري', MAD: 'درهم مغربي', TND: 'دينار تونسي',
    EGP: 'جنيه مصري', LYD: 'دينار ليبي', SAR: 'ريال سعودي',
    AED: 'درهم إماراتي', QAR: 'ريال قطري', KWD: 'دينار كويتي',
    OMR: 'ريال عماني', BHD: 'دينار بحريني', YER: 'ريال يمني',
    TRY: 'ليرة تركية', USD: 'دولار أمريكي', EUR: 'يورو',
    GBP: 'جنيه استرليني', CAD: 'دولار كندي', CHF: 'فرنك سويسري',
    JPY: 'ين ياباني', CNY: 'يوان صيني', AUD: 'دولار أسترالي',
    PKR: 'روبية باكستانية', INR: 'روبية هندية',
};

/**
 * Convert a price from one currency to another
 * Uses USD as the intermediate (base) rate
 */
export function convertCurrency(amount: number, from: string, to: string): number {
    if (from === to) return amount;

    const fromRate = EXCHANGE_RATES[from];
    const toRate = EXCHANGE_RATES[to];

    if (!fromRate || !toRate) return amount; // Unknown currency, return as-is

    // Convert: amount → USD → target
    const amountInUSD = amount / fromRate;
    return Math.round(amountInUSD * toRate * 100) / 100;
}

/**
 * Detect currency from browser locale
 */
export function detectCurrencyFromLocale(): string | null {
    if (typeof navigator === 'undefined') return null;

    const locale = navigator.language || '';
    // Extract country code from locale (e.g., 'ar-DZ' → 'DZ', 'fr-FR' → 'FR')
    const parts = locale.split('-');
    const country = (parts.length > 1 ? parts[1] : parts[0])?.toUpperCase() || '';

    return COUNTRY_CURRENCY_MAP[country] || null;
}
